import decodeJWT from "jwt-decode";
import httpService from "../services/http";
import { getAppConfig } from "./configuration";

export interface AuthStateInterface {
  authToken: string;
  expiration: number;
  refreshToken: string;
  lastRefreshDate: number;
}

export const getAuthInfo = (): AuthStateInterface => ({
  authToken: localStorage.getItem("idToken") ?? "",
  refreshToken: localStorage.getItem("refreshToken") ?? "",
  expiration: localStorage.getItem("expires") ? Number(localStorage.getItem("expires")) * 1000 : Date.now() - 1000,
  lastRefreshDate: localStorage.getItem("lastRefreshDate")
    ? Number(localStorage.getItem("lastRefreshDate"))
    : null
});

export const isUserAuthenticated = () => {
  const auth = getAuthInfo();
  const today = new Date().getDate();

  return (
    Boolean(auth.authToken) &&
    Boolean(auth.expiration) &&
    auth.expiration > Date.now() &&
    (auth.lastRefreshDate ? auth.lastRefreshDate === today : true)
  );
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const logUserLogout = async (reason: any) => {
  if (isUserAuthenticated()) {
    try {
      await httpService.request<unknown>({
        method: "post",
        apiUrlKey: "baseUrl",
        relativePath: `/v1/event/user-logout`,
        data: { reason },
      });
    } catch {
      /* empty */
    }
  }
};

const clearAuthLocalStorage = () => {
  const keysToRemove = [
    'loginEmail',
    'isXBSEmployee',
    'idToken',
    'expires',
    'accessToken',
    'redirectFlag',
    'refreshToken',
    'lastRefreshDate'
  ];

  keysToRemove.forEach(key => localStorage.removeItem(key));
};

export const redirectToLogin = async () => {
  const { loginStackRelativeUrl, exacteraDomainUrl, xbsEnvironmentShort, authUrl } = getAppConfig();
  const { authToken } = getAuthInfo();

  if (!loginStackRelativeUrl || !exacteraDomainUrl) return;

  // Attempt to log out from the auth service if an auth token exists
  if (authToken) {
    try {
      await fetch(`${authUrl}/v1/logout`, {
        method: "GET",
        mode: "cors",
        headers: {
          Authorization: `Bearer ${String(authToken)}`,
        },
      });
    } catch {
      // No action needed if logout fails
    }
  }

  clearAuthLocalStorage();
  const currentEnv = window.location.hostname;
  const redirectUrl =
    xbsEnvironmentShort === "prod"
      ? `https://itp-word-add-in.${exacteraDomainUrl}`
      : `https://itp-word-add-in.${xbsEnvironmentShort}.${exacteraDomainUrl}`;

  if (currentEnv === "localhost" || currentEnv.includes("exactera")) {
    window.location.replace(`${window.location.origin}/${loginStackRelativeUrl}`);
  } else {
    window.location.replace(redirectUrl);
  }
};

export interface JWTToken {
  email: string;
  userId: string;
  role: string;
}

export const decodeTokens = (token: string) => {
  try {
    const jwt = decodeJWT(token);

    if (!jwt) {
      return null;
    }

    return {
      email: (jwt as JWTToken).email,
      userId: (jwt as JWTToken).userId,
      role: (jwt as JWTToken).role,
    };
  } catch {
    return null;
  }
};

export const setAuthTokens = ({
  authToken = "",
  refreshToken = "",
  expiration,
}: Partial<AuthStateInterface>): AuthStateInterface => {
  localStorage.setItem("idToken", String(authToken));
  localStorage.setItem("refreshToken", String(refreshToken));
  localStorage.setItem("expires", expiration ? `${expiration / 1000}` : "");
  localStorage.setItem("lastRefreshDate", String(new Date().getDate()));

  return getAuthInfo();
};
